import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, Play, Pause, RotateCcw, ChevronLeft, ChevronRight, ArrowRight,
  Sparkles, Coffee, Loader2, ShieldCheck, TrendingUp,
} from "lucide-react";
import { Button } from "../ui/button";

/**
 * ProductDemoPlayer — an in-app, autoplaying ~60s cinematic POV walkthrough of
 * a Business Plan being created (a coffee shop, "Bean & Bloom") and the
 * resulting sections, built from REAL screenshots of the running application
 * (captured into /public/demo via scripts/capture_demo.py). Each image scene
 * gets a gentle Ken-Burns pan so it reads like a screen recording, framed in a
 * browser chrome, with captions, segment progress and a CTA.
 *
 * Opened from the Landing hero ("Watch the 60-sec demo") and the empty
 * Dashboard state for brand-new founders.
 */

const TICK = 50; // ms
const IMG = (f) => `${process.env.PUBLIC_URL || ""}/demo/${f}`;
const BIZ_URL = "threelilbrds.com/business/bean-and-bloom/plan";

const SCENES = [
  { id: "title", type: "title", label: "Intro", duration: 4500 },
  { id: "wizard", type: "image", label: "The wizard", duration: 6000,
    img: "01-wizard.jpg", url: "threelilbrds.com/wizard/new",
    eyebrow: "Step 1 · Tell us your idea", caption: "Answer a quick 5-step wizard" },
  { id: "generating", type: "generating", label: "Generating", duration: 3500 },
  { id: "overview", type: "image", label: "Your plan", duration: 6000,
    img: "02-plan-top.jpg", eyebrow: "Bean & Bloom", caption: "A complete plan, neatly organized" },
  { id: "market", type: "image", label: "Market", duration: 5500,
    img: "04-market.jpg", eyebrow: "Strategy", caption: "Market analysis & competitors" },
  { id: "financials", type: "image", label: "Financials", duration: 5500,
    img: "06-financials.jpg", eyebrow: "Financials", caption: "3-year revenue projections" },
  { id: "budget", type: "image", label: "Budget & runway", duration: 5500,
    img: "07-budget.jpg", eyebrow: "Financials", caption: "Budget, burn-rate & runway" },
  { id: "investment", type: "image", label: "Investment strategy", duration: 5500,
    img: "11-investment.jpg", eyebrow: "Funding", caption: "Investment strategy & investor scenarios" },
  { id: "loans", type: "image", label: "Loans", duration: 5500,
    img: "17-loans.jpg", url: "threelilbrds.com/business/bean-and-bloom/financing",
    eyebrow: "Funding", caption: "Model loans, interest & repayment schedules" },
  { id: "crm", type: "image", label: "CRM", duration: 5500,
    img: "18-crm.jpg", url: "threelilbrds.com/business/bean-and-bloom/crm",
    eyebrow: "Sales", caption: "A CRM pipeline that feeds your revenue projection" },
  { id: "roadmap", type: "image", label: "Roadmap", duration: 5500,
    img: "08-roadmap.jpg", url: "threelilbrds.com/business/bean-and-bloom/roadmap",
    eyebrow: "Execute", caption: "A step-by-step launch roadmap" },
  { id: "gantt", type: "image", label: "Gantt timeline", duration: 5500,
    img: "10-gantt.jpg", url: "threelilbrds.com/business/bean-and-bloom/roadmap",
    eyebrow: "Execute", caption: "A Gantt timeline with dependencies" },
  { id: "recap", type: "recap", label: "Investor-ready", duration: 8000 },
];

const RESULT_SECTIONS = [
  "Executive Summary", "Market Analysis", "Competitors", "Customer Acquisition",
  "Operations", "Revenue Projections", "Budget & Runway", "Investment Strategy",
  "Loans", "CRM Pipeline", "Roadmap", "Gantt Timeline",
];

export default function ProductDemoPlayer({ open, onClose, onCta }) {
  const [index, setIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [finished, setFinished] = useState(false);
  const scrollLock = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(0); setElapsed(0); setPlaying(true); setFinished(false);
      scrollLock.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else if (scrollLock.current !== null) {
      document.body.style.overflow = scrollLock.current;
    }
    return () => {
      if (scrollLock.current !== null) document.body.style.overflow = scrollLock.current;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !playing || finished) return undefined;
    const t = setInterval(() => setElapsed((e) => e + TICK), TICK);
    return () => clearInterval(t);
  }, [open, playing, finished, index]);

  useEffect(() => {
    if (!open) return;
    if (elapsed >= SCENES[index].duration) {
      if (index >= SCENES.length - 1) { setPlaying(false); setFinished(true); }
      else { setIndex((i) => i + 1); setElapsed(0); }
    }
  }, [elapsed, index, open]);

  const jump = useCallback((i) => {
    setIndex(Math.max(0, Math.min(SCENES.length - 1, i)));
    setElapsed(0); setFinished(false); setPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    if (finished) { jump(0); return; }
    setPlaying((p) => !p);
  }, [finished, jump]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === " ") { e.preventDefault(); togglePlay(); }
      else if (e.key === "ArrowRight") jump(index + 1);
      else if (e.key === "ArrowLeft") jump(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, togglePlay, jump, onClose]);

  if (!open) return null;
  const scene = SCENES[index];
  const progress = Math.min(1, elapsed / scene.duration);
  const url = scene.url || BIZ_URL;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(10,10,10,0.92)" }}
      data-testid="product-demo-player"
      role="dialog"
      aria-label="Product demo"
    >
      <DemoStyles />
      <div className="w-full max-w-5xl">
        <div className="flex gap-1.5 mb-3" data-testid="demo-progress">
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => jump(i)}
              className="h-1.5 flex-1 bg-white/20 overflow-hidden"
              aria-label={`Jump to ${s.label}`}
              data-testid={`demo-seg-${i}`}
            >
              <span className="block h-full bg-white" style={{ width: i < index ? "100%" : i === index ? `${progress * 100}%` : "0%" }} />
            </button>
          ))}
        </div>

        <div className="bg-white border-2 border-neutral-900 shadow-[10px_10px_0_0_rgba(124,95,176,0.45)]">
          <div className="flex items-center gap-2 px-3 py-2 bg-neutral-900">
            <span className="w-3 h-3 rounded-full bg-red-400" />
            <span className="w-3 h-3 rounded-full bg-amber-400" />
            <span className="w-3 h-3 rounded-full bg-emerald-400" />
            <div className="ml-2 flex-1 text-[11px] text-neutral-300 bg-neutral-800 px-3 py-1 rounded-sm truncate font-mono">{url}</div>
          </div>
          <div
            key={scene.id}
            className="relative h-[58vh] sm:h-[62vh] overflow-hidden bg-neutral-900"
            data-testid={`demo-scene-${scene.id}`}
          >
            {scene.type === "title" && <SceneTitle />}
            {scene.type === "generating" && <SceneGenerating />}
            {scene.type === "recap" && <SceneRecap onCta={onCta} />}
            {scene.type === "image" && (
              <>
                <img
                  src={IMG(scene.img)}
                  alt={scene.caption}
                  className="absolute inset-0 w-full h-full object-cover object-top pd-kenburns"
                  style={{ animationDuration: `${scene.duration + 600}ms` }}
                  data-testid="demo-screenshot"
                />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C4B0E6] mb-1" style={fade(150)}>{scene.eyebrow}</div>
                  <div className="text-white font-display font-black text-xl sm:text-3xl tracking-tighter" style={fade(280)}>{scene.caption}</div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 text-white">
          <div className="flex items-center gap-2">
            <button onClick={() => jump(index - 1)} disabled={index === 0} className="p-2 hover:bg-white/10 disabled:opacity-30" data-testid="demo-prev" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={togglePlay} className="p-2 hover:bg-white/10" data-testid="demo-playpause" aria-label={playing ? "Pause" : "Play"}>
              {finished ? <RotateCcw className="w-5 h-5" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button onClick={() => jump(index + 1)} disabled={index === SCENES.length - 1} className="p-2 hover:bg-white/10 disabled:opacity-30" data-testid="demo-next" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/70 ml-1 hidden sm:inline">{index + 1} / {SCENES.length} · {scene.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onCta} className="rounded-none bg-white text-neutral-900 hover:bg-neutral-200 h-9 px-4 text-[11px] uppercase tracking-[0.2em] font-bold" data-testid="demo-cta">
              Start your free plan <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button onClick={onClose} className="p-2 hover:bg-white/10" data-testid="demo-close" aria-label="Close demo"><X className="w-5 h-5" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function fade(delay) {
  return { animation: "pdFadeUp 0.6s ease both", animationDelay: `${delay}ms` };
}

function SceneTitle() {
  return (
    <div className="h-full w-full p-6 sm:p-10 flex flex-col items-center justify-center text-center bg-neutral-900 text-white">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[#C4B0E6] mb-6" style={fade(100)}>
        <Sparkles className="w-3.5 h-3.5" /> Three Little Birds · Live product demo
      </div>
      <Coffee className="w-12 h-12 text-amber-400 mb-5" style={fade(250)} />
      <h2 className="font-display font-black tracking-tighter text-3xl sm:text-5xl leading-tight max-w-2xl" style={fade(400)}>
        Watch a coffee shop go from <span className="text-[#C4B0E6] italic">idea</span> to a full business plan
      </h2>
      <p className="text-neutral-400 mt-5 text-sm sm:text-base max-w-md" style={fade(650)}>
        Real screens from the app — meet <b className="text-white">Bean &amp; Bloom</b>. In ~60 seconds: the wizard, the draft, and every section it produces.
      </p>
    </div>
  );
}

function SceneGenerating() {
  return (
    <div className="h-full w-full p-6 sm:p-10 flex flex-col items-center justify-center text-center bg-neutral-900 text-white">
      <div className="relative mb-6" style={fade(50)}>
        <Loader2 className="w-14 h-14 text-[#C4B0E6] animate-spin" />
        <Coffee className="w-6 h-6 text-amber-400 absolute inset-0 m-auto" />
      </div>
      <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tighter" style={fade(150)}>Drafting Bean &amp; Bloom's plan…</h3>
      <p className="text-neutral-400 text-sm mt-2" style={fade(300)}>Three Little Birds is writing your sections, budget and roadmap.</p>
      <div className="mt-7 w-full max-w-lg space-y-2.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-3 bg-white/10 overflow-hidden relative" style={fade(400 + i * 150)}><span className="absolute inset-0 pd-shimmer" /></div>
        ))}
      </div>
    </div>
  );
}

function SceneRecap({ onCta }) {
  return (
    <div className="h-full w-full p-6 sm:p-10 flex flex-col items-center justify-center text-center bg-neutral-900 text-white">
      <div className="inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold mb-5" style={fade(50)}>
        <ShieldCheck className="w-4 h-4" /> A complete, investor-ready plan
      </div>
      <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter max-w-2xl" style={fade(200)}>
        Nine sections. One coffee shop. <span className="text-[#C4B0E6]">Sixty seconds.</span>
      </h3>
      <div className="flex flex-wrap gap-2 justify-center mt-6 max-w-xl">
        {RESULT_SECTIONS.map((s, i) => (
          <span key={s} className="inline-flex items-center gap-1 bg-white/10 border border-white/15 px-3 py-1.5 text-xs" style={fade(350 + i * 70)}>
            <TrendingUp className="w-3 h-3 text-[#C4B0E6]" /> {s}
          </span>
        ))}
      </div>
      <Button onClick={onCta} className="mt-8 rounded-none bg-white text-neutral-900 hover:bg-neutral-200 h-12 px-7 text-[12px] uppercase tracking-[0.25em] font-bold" style={fade(900)} data-testid="demo-recap-cta">
        Start your free plan <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

function DemoStyles() {
  return (
    <style>{`
      @keyframes pdFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes pdShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
      @keyframes pdKenburns { from { transform: scale(1.02) translateY(0); } to { transform: scale(1.12) translateY(-9%); } }
      .pd-shimmer { background: linear-gradient(90deg, transparent, rgba(196,176,230,0.5), transparent); animation: pdShimmer 1.2s ease-in-out infinite; }
      .pd-kenburns { animation: pdKenburns linear both; transform-origin: top center; }
    `}</style>
  );
}
